import toast from 'react-hot-toast';
import { CREATOR_APPLY_PATH, CREATOR_PATHS } from '../../../config/creatorProgramPaths';
import {
  resolveSellerStudioPath,
  SELLER_APPLY_PATH,
} from '../../../config/sellerProgramPaths';
import { UserRole, type IUser } from '../../../types/auth.types';
import { hasCreatorPortalRole } from '../../../utils/creatorPortalAccess';
import type { AuthPromptOpenOptions, AuthPromptUserStatus } from './authPrompt.types';

export type AuthPromptGuardContext = {
  isAuthenticated: boolean;
  user: IUser | null;
};

export type AuthPromptGuardResult =
  | { type: 'show'; options: AuthPromptOpenOptions }
  | { type: 'run_success'; onSuccess?: () => void | Promise<void> }
  | { type: 'redirect'; path: string }
  | { type: 'blocked'; message: string };

export function resolveAuthPromptUserStatus(
  ctx: AuthPromptGuardContext,
): AuthPromptUserStatus {
  if (!ctx.isAuthenticated || !ctx.user) return 'guest';
  if (ctx.user.isEmailVerified === false) return 'unverified';
  return 'logged_in';
}

function isSellerSuspended(_user: IUser): boolean {
  // Seller suspension is enforced server-side; account status is not on IUser yet.
  return false;
}

export function evaluateAuthPromptGuard(
  options: AuthPromptOpenOptions,
  ctx: AuthPromptGuardContext,
): AuthPromptGuardResult {
  const { moment, onSuccess } = options;

  if (!ctx.isAuthenticated || !ctx.user) {
    return { type: 'show', options };
  }

  if (ctx.user.isEmailVerified === false) {
    return {
      type: 'blocked',
      message: 'Please verify your email before continuing. Check your inbox for the verification link.',
    };
  }

  if (!ctx.user.isActive) {
    return {
      type: 'blocked',
      message: 'Your account is not active. Contact support if you need help.',
    };
  }

  if (moment === 'seller_program' || moment === 'publish_design') {
    if (isSellerSuspended(ctx.user)) {
      return {
        type: 'blocked',
        message: 'Your seller account is suspended. Contact support to resolve this.',
      };
    }
    if (ctx.user.roles?.includes(UserRole.SELLER)) {
      return {
        type: 'redirect',
        path: resolveSellerStudioPath(
          Boolean(ctx.user.sellerOnboardingCompletedAt),
        ),
      };
    }
    return { type: 'redirect', path: SELLER_APPLY_PATH };
  }

  if (moment === 'creator_program') {
    if (hasCreatorPortalRole(ctx.user.roles)) {
      return { type: 'redirect', path: CREATOR_PATHS.dashboard };
    }
    return { type: 'redirect', path: CREATOR_APPLY_PATH };
  }

  return { type: 'run_success', onSuccess };
}

export function applyAuthPromptGuardResult(
  result: AuthPromptGuardResult,
  navigate: (path: string) => void,
): boolean {
  switch (result.type) {
    case 'show':
      return true;
    case 'run_success':
      void Promise.resolve(result.onSuccess?.()).catch(() => undefined);
      return false;
    case 'redirect':
      navigate(result.path);
      return false;
    case 'blocked':
      toast.error(result.message);
      return false;
    default:
      return false;
  }
}
